import { useState } from "react";
import { Skeleton, Stack, Text } from "@mantine/core";
import { Alert, ConfirmModal, PageHeader } from "@xplatform/design-system";
import { IconCloud } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { CredentialWrite, ProviderAdapter, ProviderCredential } from "@models/providerCatalog";
import { providerCatalogApi } from "@services/providerCatalogApi";
import { errorMessage, useApiResource } from "@hooks/monitoring/useApiResource";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { AdminPermissions } from "@config/permissions";
import { CredentialModal, EncryptionKeyCard, ProviderSection } from "@features/Providers";

/**
 * Providers are the adapters the runtime can call (Azure OpenAI, Gemini, OpenAI,
 * Bedrock, Document Intelligence); they are not created here. What an admin
 * manages is their credentials, each granted to modules (created under Access > Modules): core writes one
 * SSM copy per module, and a module's service can call the provider only with a
 * credential granted to it (there is no global one).
 */
export const ProviderManagementPage = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAdminPermissions();
  const canManage = hasPermission(AdminPermissions.PROVIDER_CREDENTIAL_MANAGE);
  const canRotateKey = hasPermission(AdminPermissions.PROVIDER_ENCRYPTION_ROTATE);
  const canViewCredentials = canManage || hasPermission(AdminPermissions.PROVIDER_CREDENTIAL_VIEW);
  const catalog = useApiResource("providers", (signal) => providerCatalogApi().catalog(signal));
  // The modules a credential can be granted to (created under Access > Modules).
  const modules = useApiResource(canViewCredentials ? "provider-modules" : null,
                                 (signal) => providerCatalogApi().modules(signal));

  const [editing, setEditing] = useState<{ provider: ProviderAdapter; credential: ProviderCredential | null } | null>(null);
  const [deleting, setDeleting] = useState<{ provider: ProviderAdapter; credential: ProviderCredential } | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const save = async (providerKey: string, credentialId: string | null, body: CredentialWrite) => {
    if (credentialId === null) await providerCatalogApi().createCredential(providerKey, body);
    else await providerCatalogApi().updateCredential(providerKey, credentialId, body);
    catalog.reload();
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await providerCatalogApi().deleteCredential(deleting.provider.key, deleting.credential.id);
      setDeleting(null);
      catalog.reload();
    } catch (err) {
      setDeleteError(errorMessage(err));
    } finally {
      setDeleteBusy(false);
    }
  };

  const deleteImpact = deleting
    ? t("admin.providers.deleteImpact", {
        provider: deleting.provider.label,
        modules: deleting.credential.modules.map((m) => m.name ?? m.id).join(", "),
      })
    : "";

  return (
    <Stack gap="md">
      <PageHeader
        title={t("admin.providers.title")}
        description={t("admin.providers.description")}
        icon={<IconCloud size={28} />}
      />

      {/* Status and rotation are Super Admin only (admin.provider.encryption.rotate). */}
      {canRotateKey && <EncryptionKeyCard canRotate={canRotateKey} />}

      {catalog.error && (
        <Alert color={catalog.forbidden ? "gray" : "red"} title={t("admin.providers.loadFailed")}>
          {catalog.error}
        </Alert>
      )}

      {catalog.loading && !catalog.data ? (
        <Stack gap="md">
          {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} h={160} radius="md" />)}
        </Stack>
      ) : (
        <Stack gap="md">
          {catalog.data?.providers.map((provider) => (
            <ProviderSection
              key={provider.key}
              provider={provider}
              canManage={canManage}
              onAdd={(p) => setEditing({ provider: p, credential: null })}
              onEdit={(p, credential) => setEditing({ provider: p, credential })}
              onDelete={(p, credential) => {
                setDeleteError(null);
                setDeleting({ provider: p, credential });
              }}
            />
          ))}
        </Stack>
      )}

      <CredentialModal
        opened={editing !== null}
        provider={editing?.provider ?? null}
        credential={editing?.credential ?? null}
        modules={modules.data ?? null}
        onClose={() => setEditing(null)}
        onSave={save}
      />

      <ConfirmModal
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t("admin.providers.deleteTitle", {
          provider: deleting?.provider.label ?? "",
          name: deleting?.credential.name ?? deleting?.credential.id ?? "",
        })}
        confirmLabel={t("admin.providers.delete")}
        cancelLabel={t("common.cancel")}
        loading={deleteBusy}
        portalProps={{ target: "#admin-portal-root" }}
        danger
      >
        <Stack gap="sm">
          <Text size="sm">{deleteImpact}</Text>
          {deleteError && <Alert color="red">{deleteError}</Alert>}
        </Stack>
      </ConfirmModal>
    </Stack>
  );
};
